import { GroupMetricsChart } from './GroupMetricsChart';
import { parseFilters } from '@utils/parseFilters';
import useToastContext from '@contexts/ToastContext/hook';
import React, { useState, useEffect } from 'react';
import { DashboardFilters } from '@interfaces/dashboard';
import { Grid } from '@mui/material';
import { findNewUsers } from '@api/reports';
import { NewUsers } from '@interfaces/reports/newUsers';
import useGroupsContext from '@contexts/GroupsContext/hook';
import { Typography } from '../_base/Typography';

type GroupMetricsChartsFilters = Pick<
  DashboardFilters,
  'botConfigIds' | 'startDate' | 'endDate'
>;
type GroupMetricsChartsProps = {
  filters: GroupMetricsChartsFilters;
};

const newUsersInitialState: NewUsers = {
  newSubscribers: 0,
  newUsers: 0,
  totalSubscribers: 0,
  totalUsers: 0,
  activeUsers: 0,
};

export function GroupMetricsCharts(props: GroupMetricsChartsProps) {
  const { filters } = props;

  const toast = useToastContext();
  const [data, setData] = useState<NewUsers>(newUsersInitialState);
  const [isLoading, setIsLoading] = useState(false);

  const { filterProductIdsByRole } = useGroupsContext();

  useEffect(() => {
    /**
     * Mesma guarda de requisição superada do SalesHistoryChart, pelo mesmo motivo.
     *
     * Sem ela, quem responde por último manda: uma requisição antiga mais lenta sobrescrevia a nova, e
     * o seu catch — que fala com o provider global de toast, vivo mesmo com este componente desmontado —
     * anunciava falha de carregamento por cima de uma seleção que tinha carregado bem.
     */
    let active = true;

    /*
      Seleção vazia sai ANTES do `parseFilters`, e isso não é redundância com a guarda de baixo.

      `parseFilters` descarta campo cujo `toString()` é vazio, e `[].toString()` é `''` — então a
      seleção vazia vira `botConfigIds: undefined`, e `filterProductIdsByRole` chama `.filter` em
      `undefined`. A tela quebra antes de chegar à guarda que existe para tratar exatamente isso.
    */
    if (!filters.botConfigIds.length) {
      setData(newUsersInitialState);
      setIsLoading(false);
      return () => {
        active = false;
      };
    }

    const parsed = parseFilters(filters);

    /*
      E a segunda guarda é sobre o que vai ser ENVIADO.

      A original olhava só o que chegou, e o recorte por papel acontecia depois: quem seleciona só
      comunidades de afiliado esvazia a lista NO RECORTE, e a requisição saía com `botConfigIds`
      vazio — pedido sem recorte nenhum, o oposto do que a guarda existe para impedir.
    */
    const botConfigIds = filterProductIdsByRole(parsed.botConfigIds, [
      'OWNER',
      'COPRODUCER',
    ]);

    if (!botConfigIds.length) {
      setData(newUsersInitialState);
      setIsLoading(false);
      return () => {
        active = false;
      };
    }

    setIsLoading(true);

    findNewUsers({ ...parsed, botConfigIds })
      .then((response) => {
        if (active) setData(response.data);
      })
      .catch((error) => {
        if (!active) return;
        toast.handleError(error, 'Não foi possível carregar os registros');
        setData(newUsersInitialState);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, [filters]);

  return (
    <section>
      <Grid container spacing={3} columnSpacing={3} alignItems="stretch">
        <Grid item xs={12} sm md>
          <GroupMetricsChart
            isLoading={isLoading}
            chartData={{
              newValue: +(data?.newUsers || 0),
              totalValue: +(data?.totalUsers || 0),
              activeUsers: +(data?.activeUsers || 0),
            }}
            label={{
              new: 'Novos Usuários',
              total: 'Total Usuários',
              activeUsers: 'Usuários ativos',
            }}
            title="Usuários"
            tooltipContent={<UsersTooltip />}
          />
        </Grid>
        <Grid item xs sm md>
          <GroupMetricsChart
            isLoading={isLoading}
            chartData={{
              newValue: +(data?.newSubscribers || 0),
              totalValue: +(data?.totalSubscribers || 0),
            }}
            label={{ new: 'Novos Assinantes', total: 'Total Assinantes' }}
            title="Assinantes"
          />
        </Grid>
      </Grid>
    </section>
  );
}

const UsersTooltip = () => (
  <div className="grid gap-sm p-sm">
    <Typography className="text-white text-xs">
      <strong>Novos usuários:</strong> Novos usuários que interagiram com seu
      bot no período selecionado.
    </Typography>
    <Typography className="text-white text-xs">
      <strong>Total de contatos:</strong> Quantidade total de usuários que já
      interagiram com seu bot.
    </Typography>
    <Typography className="text-white text-xs">
      <strong>Contatos ativos:</strong> Usuários que o bot consegue fazer
      contato.
    </Typography>
  </div>
);
